import React from "react";

import { Grid, MenuItem } from "@mui/material";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import GeneralCommentsComponent from "../../../../../../../../components/Generic/GeneralComments";
import { formatInTimeZone } from "../../../../../../../../util/formatInTimezone";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import {
  CommentsContainer,
  DetailsFieldsContainer,
  Separator,
  StorageAccountsDetailsTitle,
  StorageAccountsDetailsWrapper,
} from "./styles";

const StorageAccountStatus = [
  { name: "Active", value: "active" },
  { name: "Draft", value: "draft" },
];

const StorageAccountStorageType = [
  { name: "LTS", value: "lts" },
  { name: "SIT", value: "sit" },
];

const StorageAccountBillingPaymentMethod = [
  { name: "Cash", value: "cash" },
  { name: "Credit Card", value: "creaditCard" },
  { name: "Bank Transfer", value: "bankTransfer" },
  { name: "EFPOS", value: "efpos" },
];

const StorageAccountBillingPlan = [
  { name: "Daily", value: "daily" },
  { name: "Weekly", value: "weekly" },
  { name: "Monthly", value: "monthly" },
  { name: "Quarterly", value: "quarterly" },
  { name: "Six Monthly", value: "sixMonthly" },
  { name: "Annually", value: "annually" },
  { name: "Once (not recurring)", value: "once" },
];

const StorageAccountBilling = [{ name: "In Arrears", value: "inArrears" }];

export const StorageAccountDetails = () => {
  const { t: translate } = useTranslation();

  return (
    <StorageAccountsDetailsWrapper container>
      <Grid container>
        <StorageAccountsDetailsTitle>{translate("details")}</StorageAccountsDetailsTitle>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <AutoSuggest
            label={`${translate("storageAccountOrder")} #`}
            placeholder={translate("search")}
            dataCy="storageAccountOrder"
            value={""}
            inputValue={""}
            getOptionLabel={() => ""}
            getOptionSelected={() => false}
            options={[]}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            data-cy="storageAccountOrderReference"
            placeholder={translate("select")}
            label={translate("referenceNumber")}
            value={""}
            onChange={() => {}}
          ></Select>
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            data-cy="storageAccountOrder"
            placeholder={translate("select")}
            label={`${translate("order")} #`}
            value={""}
            onChange={() => {}}
          ></Select>
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            data-cy="storageAccountCustomer"
            placeholder={translate("customerName")}
            label={translate("customer")}
            disabled
            value={""}
            onChange={() => {}}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Input
            data-cy="storageAccountOrderType"
            placeholder={translate("orderType")}
            label={translate("orderType")}
            disabled
            value={""}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            data-cy="storageAccountStorageStatus"
            placeholder={translate("storageStatus")}
            label={translate("storageStatus")}
            disabled
            value={""}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            data-cy="storageAccountPickedUp"
            placeholder={translate("pickedUp")}
            label={translate("pickedUp")}
            disabled
            value={""}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            data-cy="storageAccountVolume"
            placeholder={translate("volume")}
            label={`${translate("volume")} (${getMeasurement()?.code})`}
            type="number"
            disabled
            value={0}
            onChange={() => {}}
          />
        </Grid>

        <DetailsFieldsContainer item container alignItems="flex-start" direction="row" spacing={2}>
          <Grid item xs={12} md>
            <Select
              data-cy="storageAccountStatus"
              placeholder={translate("select")}
              label={translate("status")}
              value={""}
              onChange={() => {}}
            >
              {StorageAccountStatus.map((status) => (
                <MenuItem key={status.value} value={status.value} data-cy={status.name}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md>
            <Select
              data-cy="storageAccountTypeStorage"
              placeholder={translate("select")}
              label={translate("typeStorage")}
              value={""}
              onChange={() => {}}
            >
              {StorageAccountStorageType.map((type) => (
                <MenuItem key={type.value} value={type.value} data-cy={type.name}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md>
            <Input
              type="date"
              data-cy="storageAccountStorageFrom"
              label={translate("storageFrom")}
              value={formatInTimeZone(
                "yyyy-MM-dd",
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                ""
              )}
              onChange={(event) =>
                formatInTimeZone(
                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                  "UTC",
                  parseISO(event.target.value)
                )
              }
            />
          </Grid>
          <Grid item xs={12} md>
            <Input
              type="date"
              data-cy="storageAccountGracePeriodEnd"
              label={translate("gracePeriodEnd")}
              value={formatInTimeZone(
                "yyyy-MM-dd",
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                ""
              )}
              onChange={(event) =>
                formatInTimeZone(
                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                  "UTC",
                  parseISO(event.target.value)
                )
              }
            />
          </Grid>
          <Grid item xs={12} md>
            <Input
              type="date"
              data-cy="storageAccountStorageEnd"
              label={translate("storageEnd")}
              value={formatInTimeZone(
                "yyyy-MM-dd",
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                ""
              )}
              onChange={(event) =>
                formatInTimeZone(
                  "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
                  "UTC",
                  parseISO(event.target.value)
                )
              }
            />
          </Grid>
        </DetailsFieldsContainer>

        <Grid item xs={12}>
          <StorageAccountsDetailsTitle>{translate("billing")}</StorageAccountsDetailsTitle>
        </Grid>
        <Grid item xs={12} md={3}>
          <Input
            data-cy="storageAccountBillingEmail"
            placeholder={translate("sample@shipeezi.com")}
            label={translate("billingEmail")}
            value={""}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            data-cy="storageAccountBillingPaymentMethod"
            placeholder={translate("selectAPaymentMethod")}
            label={translate("paymentMethod")}
            value={""}
            onChange={() => {}}
          >
            {StorageAccountBillingPaymentMethod.map((method) => (
              <MenuItem key={method.value} value={method.value} data-cy={method.name}>
                {method.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            data-cy="storageAccountBillingPlan"
            placeholder={translate("select")}
            label={translate("plan")}
            value={""}
            onChange={() => {}}
          >
            {StorageAccountBillingPlan.map((plan) => (
              <MenuItem key={plan.value} value={plan.value} data-cy={plan.name}>
                {plan.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} md={3}>
          <Select
            data-cy="storageAccountBilling"
            placeholder={translate("select")}
            label={translate("billing")}
            value={""}
            onChange={() => {}}
          >
            {StorageAccountBilling.map((billing) => (
              <MenuItem key={billing.value} value={billing.value} data-cy={billing.name}>
                {billing.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12}>
          <Separator />
        </Grid>

        <CommentsContainer item xs={12}>
          <GeneralCommentsComponent
            comments={[]}
            generalCommentsTitle={translate("comments")}
            onAddComment={() => {}}
            onRemoveComment={() => {}}
          />
        </CommentsContainer>
      </Grid>
    </StorageAccountsDetailsWrapper>
  );
};
